import type React from "react";
import { useMemo, useRef, useState } from "react";
import type { UseFormTrigger } from "react-hook-form";
import {
  type CountryIso2,
  defaultCountries,
  parseCountry,
  usePhoneInput,
} from "react-international-phone";
import { mergeRefs } from "react-merge-refs";
import {
  type BaseTextFieldProps,
  InputAdornment,
  TextField,
} from "@mui/material";
import classNames from "classnames";

import DropdownBlock from "@/base/components/Form/v1/PhoneField/DropdownBlock";
import DropdownButton from "@/base/components/Form/v1/PhoneField/DropdownButton";
import { useStyles as useTextFieldStyles } from "@/base/components/Form/v1/TextField";
import { useAppSelector } from "@/store";
import { selectCurrentRegionPrefix, selectRegions } from "@/store/region";
import { createUseStyles } from "@/theme";

type StyleProps = {
  isFocused: boolean;
  hasError?: boolean;
  isDisabled?: boolean;
};

export type PhoneFieldProps = {
  value?: string;
  onChange?: (e: { target: { value: string } }) => void;
  innerRef?: React.RefCallback<HTMLInputElement>;
  onEnter?: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    trigger: UseFormTrigger<any>;
    name: string;
  };
  submitOnEnter?: boolean;
  hasError?: boolean;
  handleKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
} & BaseTextFieldProps;

const useStyle = createUseStyles<StyleProps>(({ color, font }) => ({
  containerOpen: {
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    borderColor: "rgba(0, 0, 0, 1)",
  },
  container: {
    fontSize: font.size.s,
    lineHeight: font.lineHeight.m,
    paddingTop: 0,
    borderColor: ({ isFocused, hasError, isDisabled }) => {
      if (hasError) {
        return color.error;
      }
      if (isFocused) {
        return color.primaryBrand;
      }
      if (isDisabled) {
        return color.gray5;
      }
      return color.gray4;
    },
  },
  textField: {
    borderWidth: ({ isFocused }) => (isFocused ? 2 : 1),
    borderStyle: "solid",
    borderColor: ({ isFocused, hasError, isDisabled }) => {
      if (hasError) {
        return color.error;
      }
      if (isFocused) {
        return color.primaryBrand;
      }
      if (isDisabled) {
        return color.gray5;
      }
      return color.gray4;
    },
  },
}));

const PhoneField: React.FC<PhoneFieldProps> = ({
  value = "",
  onChange,
  innerRef,
  onEnter,
  submitOnEnter,
  handleKeyDown,
  onBlur,
  hasError,
  ...restProps
}) => {
  const currentRegionPrefix = useAppSelector(selectCurrentRegionPrefix);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(restProps.focused);
  const regions = useAppSelector(selectRegions);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const defaultCountry = currentRegionPrefix
    ? (currentRegionPrefix as CountryIso2)
    : "ee";
  const preferredCountries = regions?.map(
    (region) => region.prefix as CountryIso2,
  );
  const sharedClasses = useTextFieldStyles();
  const classes = useStyle({
    isFocused: Boolean(isDropdownOpen || isFocused),
    hasError: hasError,
    isDisabled: restProps.disabled,
  });
  const preparedCountries = useMemo(
    () =>
      preferredCountries
        ? [
            ...defaultCountries.filter((country) => {
              const { iso2 } = parseCountry(country);
              return preferredCountries.includes(iso2);
            }),
            ...defaultCountries.filter((country) => {
              const { iso2 } = parseCountry(country);
              return !preferredCountries.includes(iso2);
            }),
          ]
        : defaultCountries,
    [preferredCountries],
  );
  const { inputValue, handlePhoneValueChange, inputRef, country, setCountry } =
    usePhoneInput({
      defaultCountry,
      value,
      countries: preparedCountries,
    });

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      if (!submitOnEnter) e.preventDefault();
      if (onEnter) {
        void onEnter.trigger(onEnter.name, { shouldFocus: true });
      }
    }
    if (handleKeyDown) handleKeyDown(e);
  };

  return (
    <div
      className={classNames([
        sharedClasses.container,
        classes.container,
        isDropdownOpen && classes.containerOpen,
      ])}
    >
      <TextField
        variant="outlined"
        label="Phone number"
        color="primary"
        placeholder="+123 456 7890"
        value={inputValue}
        error={hasError}
        onChange={(e) => {
          handlePhoneValueChange(e);
          if (onChange) {
            void onChange(e);
          }
        }}
        type="tel"
        inputRef={mergeRefs([inputRef, innerRef])}
        focused={isFocused || isDropdownOpen}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <DropdownButton
                country={country}
                setOpen={setIsDropdownOpen}
                isOpen={isDropdownOpen}
                isDisabled={restProps.disabled}
                buttonRef={buttonRef}
              />
            </InputAdornment>
          ),
        }}
        onKeyDown={onKeyDown}
        onFocus={() => setIsFocused(true)}
        onBlur={(e) => {
          setIsFocused(false);
          if (onBlur) {
            onBlur(e);
          }
        }}
        {...restProps}
      />
      {isDropdownOpen && (
        <DropdownBlock
          countries={preparedCountries}
          setCountry={setCountry}
          setOpen={setIsDropdownOpen}
          buttonRef={buttonRef}
        />
      )}
    </div>
  );
};

export default PhoneField;
